<template>
  <div class="fin-results" v-if="data">
    <!-- Resumen -->
    <div class="summary-bar">
      <div class="summary-item">
        <i class="pi pi-file-excel" />
        <span>{{ data.filename }}</span>
      </div>
      <div v-if="data.period?.label" class="summary-item period-badge">
        <i class="pi pi-calendar" />
        <span>{{ data.period.label }}</span>
        <span v-if="data.period.start" class="period-dates-small">
          ({{ formatDate(data.period.start) }} – {{ formatDate(data.period.end) }})
        </span>
      </div>
      <div class="summary-item">
        <i class="pi pi-shield" />
        <span>SHA-256: {{ data.sha256?.slice(0, 12) }}...</span>
      </div>
      <Tag :value="data.framework" severity="info" />
    </div>

    <!-- Tabs de resultados -->
    <TabView>
      <!-- Indicadores -->
      <TabPanel header="📊 Indicadores">
        <div class="indicator-toolbar" v-if="hasNotAvailableIndicators">
          <Button
            size="small"
            severity="secondary"
            outlined
            :label="showNotAvailableIndicators ? 'Ocultar N/D' : 'Mostrar N/D'"
            :icon="showNotAvailableIndicators ? 'pi pi-eye-slash' : 'pi pi-eye'"
            @click="showNotAvailableIndicators = !showNotAvailableIndicators"
          />
        </div>

        <div v-for="group in filteredIndicatorGroups" :key="group.category" class="indicator-group">
          <h4 class="cat-title">{{ categoryLabel(group.category) }}</h4>
          <div class="indicators-grid">
            <div v-for="ind in group.indicators" :key="ind.name" :class="['indicator-card', ind.status]">
              <div class="ind-header">
                <span class="ind-status-dot" :class="ind.status" />
                <span class="ind-name">{{ ind.name }}</span>
              </div>
              <div class="ind-value">
                {{ ind.value !== null ? `${formatNum(ind.value)} ${ind.unit}` : 'N/D' }}
              </div>
              <div class="ind-interp">{{ ind.interpretation }}</div>
              <div v-if="ind.benchmark_min !== null" class="ind-bench">
                Rango ideal: {{ ind.benchmark_min }} – {{ ind.benchmark_max }} {{ ind.unit }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="!filteredIndicatorGroups.length" class="empty-msg">
          <p>No hay indicadores para mostrar con el filtro actual.</p>
        </div>
      </TabPanel>

      <!-- Anomalías -->
      <TabPanel :header="`⚠️ Anomalías (${data.anomalies?.total ?? 0})`">
        <div v-if="!data.anomalies?.total" class="empty-msg">
          <i class="pi pi-check-circle" style="color:#10b981;font-size:2rem" />
          <p>No se detectaron anomalías.</p>
        </div>
        <div v-else>
          <div class="anomaly-summary">
            <Tag v-if="data.anomalies.critical" :value="`${data.anomalies.critical} críticas`" severity="danger" />
            <Tag v-if="data.anomalies.high" :value="`${data.anomalies.high} altas`" severity="warning" />
            <Tag v-if="data.anomalies.medium" :value="`${data.anomalies.medium} medias`" severity="info" />
          </div>
          <div v-for="(a, i) in data.anomalies.items" :key="i" :class="['anomaly-item', a.severity]">
            <div class="anomaly-header">
              <span :class="['sev-badge', a.severity]">{{ a.severity.toUpperCase() }}</span>
              <span class="anomaly-type">{{ anomalyLabel(a.anomaly_type) }}</span>
            </div>
            <p class="anomaly-desc">{{ a.description }}</p>
            <p v-if="a.recommendation" class="anomaly-rec">
              <i class="pi pi-lightbulb" /> {{ a.recommendation }}
            </p>
          </div>
        </div>
      </TabPanel>

      <!-- Cumplimiento -->
      <TabPanel :header="`✅ Cumplimiento (${data.compliance?.compliance_score ?? 0}%)`">
        <div class="compliance-score">
          <div class="score-circle" :class="scoreClass(data.compliance?.compliance_score)">
            {{ data.compliance?.compliance_score?.toFixed(1) }}%
          </div>
          <div class="score-stats">
            <span class="stat ok">✅ {{ data.compliance?.passed }} cumple</span>
            <span class="stat fail">❌ {{ data.compliance?.failed }} incumple</span>
            <span class="stat partial">⚠️ {{ data.compliance?.partial }} parcial</span>
          </div>
        </div>
        <div v-for="(f, i) in data.compliance?.findings" :key="i" :class="['finding-item', f.status]">
          <div class="finding-header">
            <span class="finding-status-icon">{{ statusIcon(f.status) }}</span>
            <span class="finding-norm">{{ f.norm }}</span>
          </div>
          <p class="finding-req">{{ f.requirement }}</p>
          <p v-if="f.recommendation" class="finding-rec">
            <i class="pi pi-arrow-right" /> {{ f.recommendation }}
          </p>
        </div>
      </TabPanel>

      <!-- Estados financieros -->
      <TabPanel header="📋 Estados">
        <div v-if="hasBalance" class="statement-section">
          <h4>Balance General</h4>
          <DataTable :value="balanceRows" size="small" responsiveLayout="scroll">
            <Column field="account" header="Cuenta" />
            <Column field="value" header="Valor">
              <template #body="{ data: row }">
                <span :class="row.value < 0 ? 'negative' : ''">
                  {{ formatCOP(row.value) }}
                </span>
              </template>
            </Column>
          </DataTable>
        </div>
        <div v-if="hasResults" class="statement-section mt-3">
          <h4>Estado de Resultados</h4>
          <DataTable :value="resultsRows" size="small" responsiveLayout="scroll">
            <Column field="account" header="Cuenta" />
            <Column field="value" header="Valor">
              <template #body="{ data: row }">
                <span :class="row.value < 0 ? 'negative' : ''">
                  {{ formatCOP(row.value) }}
                </span>
              </template>
            </Column>
          </DataTable>
        </div>
      </TabPanel>

      <!-- Notas a los estados financieros -->
      <TabPanel header="📝 Notas">
        <FinancialNotes v-if="data.document_id" :document-id="data.document_id" />
        <p v-else class="empty-msg">No hay documento asociado para agregar notas.</p>
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import FinancialNotes from './FinancialNotes.vue';

const props = defineProps({
  data: { type: Object, default: null },
});

const showNotAvailableIndicators = ref(true);

const categoryLabel = (cat) => ({
  liquidez: '💧 Liquidez',
  rentabilidad: '📈 Rentabilidad',
  endeudamiento: '🏦 Endeudamiento',
  actividad: '⚙️ Actividad',
}[cat] || cat);

const anomalyLabel = (type) => ({
  ecuacion_contable: 'Ecuación Contable',
  valor_negativo: 'Valor Negativo',
  cambio_abrupto: 'Cambio Abrupto',
  benford: 'Ley de Benford',
  duplicado: 'Duplicado',
}[type] || type);

const statusIcon = (s) => ({ compliant: '✅', non_compliant: '❌', partial: '⚠️' }[s] || '❓');

const scoreClass = (score) => {
  if (!score) return 'score-na';
  if (score >= 80) return 'score-good';
  if (score >= 50) return 'score-warn';
  return 'score-bad';
};

const formatNum = (v) => {
  if (v === null || v === undefined) return 'N/D';
  return Number.isInteger(v) ? v.toLocaleString('es-CO') : v.toFixed(2);
};

const formatCOP = (v) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v);
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const hasNotAvailableIndicators = computed(() =>
  Object.values(props.data?.indicators || {}).some((inds) =>
    (inds || []).some((ind) => ind?.value === null || ind?.value === undefined)
  )
);

const filteredIndicatorGroups = computed(() => {
  const groups = Object.entries(props.data?.indicators || {}).map(([category, indicators]) => {
    const filteredIndicators = (indicators || []).filter((ind) =>
      showNotAvailableIndicators.value || (ind?.value !== null && ind?.value !== undefined)
    );
    return { category, indicators: filteredIndicators };
  });
  return groups.filter((group) => group.indicators.length > 0);
});

const hasBalance = computed(() => Object.keys(props.data?.statements?.balance_general || {}).length > 0);
const hasResults = computed(() => Object.keys(props.data?.statements?.estado_resultados || {}).length > 0);

const balanceRows = computed(() =>
  Object.entries(props.data?.statements?.balance_general || {}).map(([account, value]) => ({ account, value }))
);
const resultsRows = computed(() =>
  Object.entries(props.data?.statements?.estado_resultados || {}).map(([account, value]) => ({ account, value }))
);
</script>

<style scoped>
.fin-results { display: flex; flex-direction: column; gap: 1rem; }

.summary-bar {
  display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
  padding: 0.75rem 1rem; background: #f8fafc; border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.summary-item { display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: #475569; }
.period-badge { background: #e0f2fe; padding: 0.3rem 0.6rem; border-radius: 6px; color: #0369a1; font-weight: 600; }
.period-dates-small { font-size: 0.75rem; color: #64748b; font-weight: 400; }

/* Indicadores */
.indicator-toolbar { display: flex; justify-content: flex-end; margin-bottom: 0.75rem; }
.indicator-group { margin-bottom: 1.5rem; }
.cat-title { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 0.75rem; }
.indicators-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0.75rem; }
.indicator-card {
  padding: 0.875rem; border-radius: 8px; border: 1px solid #e5e7eb;
  background: white; transition: box-shadow 0.2s;
}
.indicator-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.indicator-card.ok { border-left: 4px solid #10b981; }
.indicator-card.warning { border-left: 4px solid #f59e0b; }
.indicator-card.critical { border-left: 4px solid #ef4444; }
.indicator-card.sin_datos { border-left: 4px solid #d1d5db; }

.ind-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.4rem; }
.ind-status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.ind-status-dot.ok { background: #10b981; }
.ind-status-dot.warning { background: #f59e0b; }
.ind-status-dot.critical { background: #ef4444; }
.ind-status-dot.sin_datos { background: #d1d5db; }
.ind-name { font-weight: 600; font-size: 0.85rem; color: #374151; }
.ind-value { font-size: 1.25rem; font-weight: 700; color: #1e293b; margin-bottom: 0.3rem; }
.ind-interp { font-size: 0.78rem; color: #6b7280; line-height: 1.4; }
.ind-bench { font-size: 0.75rem; color: #9ca3af; margin-top: 0.3rem; }

/* Anomalías */
.anomaly-summary { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1rem; }
.anomaly-item { padding: 0.875rem; border-radius: 8px; margin-bottom: 0.75rem; border: 1px solid; }
.anomaly-item.critical { background: #fef2f2; border-color: #fecaca; }
.anomaly-item.high { background: #fff7ed; border-color: #fed7aa; }
.anomaly-item.medium { background: #fffbeb; border-color: #fde68a; }
.anomaly-item.low { background: #eff6ff; border-color: #bfdbfe; }
.anomaly-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.4rem; }
.sev-badge { font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: 4px; }
.sev-badge.critical { background: #ef4444; color: white; }
.sev-badge.high { background: #f97316; color: white; }
.sev-badge.medium { background: #f59e0b; color: white; }
.sev-badge.low { background: #3b82f6; color: white; }
.anomaly-type { font-weight: 600; font-size: 0.85rem; color: #374151; }
.anomaly-desc { font-size: 0.85rem; color: #4b5563; margin: 0.25rem 0; }
.anomaly-rec { font-size: 0.8rem; color: #6b7280; font-style: italic; margin: 0; }

/* Cumplimiento */
.compliance-score { display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; }
.score-circle {
  width: 80px; height: 80px; border-radius: 50%; display: flex; align-items: center;
  justify-content: center; font-size: 1.1rem; font-weight: 700; flex-shrink: 0;
}
.score-good { background: #d1fae5; color: #065f46; border: 3px solid #10b981; }
.score-warn { background: #fef3c7; color: #92400e; border: 3px solid #f59e0b; }
.score-bad { background: #fee2e2; color: #991b1b; border: 3px solid #ef4444; }
.score-na { background: #f3f4f6; color: #6b7280; border: 3px solid #d1d5db; }
.score-stats { display: flex; flex-direction: column; gap: 0.3rem; }
.stat { font-size: 0.875rem; }
.stat.ok { color: #065f46; }
.stat.fail { color: #991b1b; }
.stat.partial { color: #92400e; }

.finding-item { padding: 0.75rem; border-radius: 6px; margin-bottom: 0.5rem; border: 1px solid #e5e7eb; }
.finding-item.compliant { background: #f0fdf4; }
.finding-item.non_compliant { background: #fef2f2; }
.finding-item.partial { background: #fffbeb; }
.finding-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.25rem; }
.finding-status-icon { font-size: 1rem; }
.finding-norm { font-weight: 700; font-size: 0.8rem; color: #374151; }
.finding-req { font-size: 0.85rem; color: #4b5563; margin: 0.2rem 0; }
.finding-rec { font-size: 0.8rem; color: #6b7280; font-style: italic; margin: 0; }

/* Estados */
.statement-section h4 { color: #1e293b; margin-bottom: 0.5rem; }
.negative { color: #ef4444; }
.mt-3 { margin-top: 1.5rem; }
.empty-msg { text-align: center; padding: 2rem; color: #6b7280; }
.empty-msg p { margin-top: 0.5rem; }
</style>
